import {useMemo, useState} from 'react'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {requestPassword} from '../core/_requests'
import LoadingButtonWrapper from '../../../components/LoadingButtonWrapper'
import {useAppIntl} from '../../../i18n'
import {createForgotPasswordSchema} from '../authSchemas'

const initialValues = {
  email: 'admin@demo.com',
}

export function ForgotPassword() {
  const {formatMessage} = useAppIntl()
  const validationSchema = useMemo(
    () => createForgotPasswordSchema(formatMessage),
    [formatMessage]
  )
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)
      setTimeout(() => {
        requestPassword(values.email)
          .then(({data: {result}}) => {
            setHasErrors(false)
            setLoading(false)
          })
          .catch(() => {
            setHasErrors(true)
            setLoading(false)
            setSubmitting(false)
            setStatus(formatMessage({id: 'auth.forgot.incorrectDetail'}))
          })
      }, 1000)
    },
  })

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        <h1 className='text-dark fw-bolder mb-3'>{formatMessage({id: 'auth.forgot.title'})}</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          {formatMessage({id: 'auth.forgot.subtitle'})}
        </div>
      </div>

      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            {formatMessage({id: 'auth.forgot.errorBanner'})}
          </div>
        </div>
      )}

      {hasErrors === false && (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>{formatMessage({id: 'auth.forgot.success'})}</div>
        </div>
      )}

      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-gray-900 fs-6'>
          {formatMessage({id: 'auth.forgot.email'})}
        </label>
        <input
          type='email'
          placeholder=''
          autoComplete='off'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>

      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <LoadingButtonWrapper isLoading={loading}>
          <button type='submit' id='kt_password_reset_submit' className='btn btn-primary me-4'>
            <span className='indicator-label'>{formatMessage({id: 'common.action.submit'})}</span>
          </button>
        </LoadingButtonWrapper>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-light'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {formatMessage({id: 'common.action.cancel'})}
          </button>
        </Link>{' '}
      </div>
    </form>
  )
}
